import {
  type ConsentOptionsListType,
  type GroupedPurposesBySection,
  mapSectionWithExtendedOptions,
  type OptionsListValue,
} from "@ui/components/ConsentSection";
import type { EligibleConsentReminderType } from "@ui/hooks/useConsentReminder";

import type { OtPurposeByUUID } from "@jti/fe-common";

export type ConsentReminderFormValues = {
  consents: Record<string, OptionsListValue | null>;
};

export const groupConsentsBySection = (consents: OtPurposeByUUID[]): GroupedPurposesBySection[] =>
  consents.reduce<GroupedPurposesBySection[]>((sections, consent) => {
    const section = sections.find((item) => item.purposeId === consent.section.id);

    if (section) {
      section.purposes.push(consent);
      return sections;
    }

    sections.push({
      purposeTitle: consent.section.name,
      purposeDescription: consent.section.description,
      purposeId: consent.section.id,
      purposes: [consent],
    });
    return sections;
  }, []);

export const getConsentUpdates = (consents: ConsentReminderFormValues["consents"]) =>
  Object.entries(consents).flatMap(([id, value]) => {
    if (value == null) {
      return [];
    }

    const [accepted, oneOff] = value.split("--");

    return [
      {
        id,
        accepted: accepted === "true",
        ...(oneOff && { one_off: oneOff === "true" }),
      },
    ];
  });

export const getDisplayedConsents = ({
  consentTypes,
  consents,
  offerConsentName,
  productInformationConsentName,
  ploomClubConsentName,
}: {
  consentTypes: EligibleConsentReminderType[];
  consents?: OtPurposeByUUID[];
  offerConsentName?: string;
  productInformationConsentName?: string;
  ploomClubConsentName?: string;
}) => {
  const consentNameByType = {
    offer_consent: offerConsentName,
    product_consent: productInformationConsentName,
    ploom_club_consent: ploomClubConsentName,
  };

  return consentTypes.flatMap((consentType) => {
    const consent = consents?.find((item) => item.name === consentNameByType[consentType]);

    return consent ? [consent] : [];
  });
};

export const getDisplayedConsentSections = (
  consents: OtPurposeByUUID[],
  consentOptionsListType: ConsentOptionsListType
) => {
  const sections = groupConsentsBySection(consents);

  if (consentOptionsListType === "extended") {
    return mapSectionWithExtendedOptions({ sections, resetDefaultValues: true });
  }

  return sections.map((section) => ({
    ...section,
    purposes: section.purposes.map((purpose) => ({
      ...purpose,
      default: null,
    })),
  }));
};

export const getConsentFormValues = (sections: GroupedPurposesBySection[]): ConsentReminderFormValues => ({
  consents: Object.fromEntries(
    sections.flatMap((section) => section.purposes.map((consent) => [consent.id, consent.default as OptionsListValue]))
  ),
});

export const getUnconfirmedMandatoryConsents = (
  consents: OtPurposeByUUID[],
  consentUpdates: ReturnType<typeof getConsentUpdates>
) => consents.filter((consent) => consent.mandatory && !consentUpdates.find(({ id }) => id === consent.id)?.accepted);
